import gql from 'graphql-tag';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import apolloClient from '@/apollo/client';
import { useCompanyStore } from '@/stores/company';
import type { SubscriptionFlowData } from '@/types';

const paymentMethods: string[] = ['credit_card', 'incasso', 'bank_transfer'];

type Country = {
  value: string;
  label: string;
};

type SubscriptionFormData = {
  billingPeriod: string;
  companyAttributes: {
    phone: string;
    country: string;
    city: string;
    postalcode: string;
    address: string;
    billingInfoAttributes: {
      iban: string;
      ibanBic: string;
      paymentMethod: string;
    };
    vatNumber: string | null;
  };
  plan: string;
};

export const useSubscriptionStore = defineStore('subscription', () => {
  const { company } = useCompanyStore();
  const countriesData = ref<Country[]>([]);
  const plansData = ref<SubscriptionFlowData['plans']>([]);
  const isSubmitted = ref(false);
  const formData = reactive<SubscriptionFormData>({
    billingPeriod: 'monthly',
    companyAttributes: {
      phone: '',
      country: company.country,
      city: '',
      postalcode: '',
      address: '',
      billingInfoAttributes: {
        iban: '',
        ibanBic: '',
        paymentMethod: paymentMethods[1],
      },
      vatNumber: null,
    },
    plan: 'pro',
  });
  const userDetailsValid = ref(false);
  const errors = reactive<Record<string, unknown>>({});

  const selectedPlanData = computed(() =>
    plansData.value.find((plan) => plan.type === formData.plan),
  );
  const cantUpgradeFromPlan = computed(() => plansData.value.length === 1);

  const getData = () => {
    const { isSubscribed } = useCompanyStore();

    apolloClient
      .query({
        query: gql`
          query getSubscriptionFlowData($upgrade: Boolean = false) {
            subscriptionFlowData(upgrade: $upgrade) {
              countries
              plans {
                chargeableEmployeeCount
                chargeableEquipmentCount
                chargeableLocationCount
                chargeableRoomCount
                features
                current
                pricings {
                  basePrice
                  currency
                  locationPrice
                  resourcePrice
                  referralReward
                  defaultPrice
                }
                type
              }
            }
          }
        `,
        variables: {
          upgrade: isSubscribed,
        },
      })
      .then(
        ({
          data: {
            subscriptionFlowData: { plans, countries },
          },
        }) => {
          const countriesArray = Object.keys(countries).map((key) => ({
            value: key,
            label: countries[key],
          }));

          countriesData.value = countriesArray.sort((a, b) =>
            a.label > b.label ? 1 : b.label > a.label ? -1 : 0,
          );
          plansData.value = plans;
        },
      );
  };

  return {
    formData,
    countriesData,
    plansData,
    selectedPlanData,
    cantUpgradeFromPlan,
    isSubmitted,
    paymentMethods,
    userDetailsValid,
    errors,
    getData,
  };
});
